import clsx from 'clsx'
import { XIcon } from 'lucide-react'
import React, { useContext } from 'react'
import { createPortal } from 'react-dom'

import { Button, useBreakpoint } from 'slab'

import { MultistepFiltersContext } from './context'

interface FilterProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  children: React.ReactNode;
  label: string;
  name: string;
  onClearValue?: () => void;
  placeholder: string;
  value?: string | null;
}

export function Filter ({
  children,
  label,
  name,
  onClearValue,
  placeholder,
  value,
  ...props
}: FilterProps) {
  const {
    activeFilter,
    contentElement,
    contentId,
    filterRef,
    setActiveFilter,
    setButtonSlot,
    setEditing
  } = useContext(MultistepFiltersContext)
  const isActive = activeFilter === name
  const { breakpointIsAtLeast } = useBreakpoint()

  function renderChildren () {
    if (!contentElement || !isActive) return null

    if (breakpointIsAtLeast('xs')) {
      return createPortal(children, contentElement)
    }

    return createPortal(
      <>
        <div className='p-4 grow'>
          {children}
        </div>
        <div className='mt-2 bg-white p-4 border-t border-gray-200 flex justify-between items-center sticky bottom-0'>
          <div>
            {value && (
              <Button variant='tertiary-deemphasized' onClick={() => onClearValue?.()}>
                Clear
              </Button>
            )}
          </div>
          <div ref={setButtonSlot} />
        </div>
      </>,
      contentElement
    )
  }

  return (
    <div
      {...props}
      className={clsx(
        'group/filter',
        'flex-1',
        'relative has-[:focus-visible]:z-[2] has-[:focus-visible]:hover:z-[2] hover:z-[1] data-[active=true]:z-[1] rounded-lg',
        'first:before:content-none hover:before:bg-transparent focus-within:before:bg-transparent before:block before:absolute before:top-0 before:-left-px before:py-4 before:bg-clip-content before:w-px before:h-full',
        {
          'bg-white hover:bg-white focus-visible:bg-white before:bg-transparent':
            isActive,
          'hover:bg-gray-50 focus-visible]:bg-gray-50 before:bg-gray-200':
            !isActive,
        }
      )}
      data-active={isActive}
    >
      <button
        aria-controls={contentId}
        aria-expanded={isActive}
        aria-haspopup='dialog'
        className={clsx(
          'block rounded-lg py-2 pl-4 pr-10 h-full w-full',
          'outline-2 -outline-offset-1 outline-transparent focus-visible:outline-thatch-500',
          'body-sm xs:body text-nowrap text-left',
          'space-y-0.5',
          'transition',
          {
            'pr-10': value,
            'pr-4': !value,
            'cursor-pointer': !isActive,
            'cursor-default': isActive,
          }
        )}
        onClick={() => {
          // If the filter was already open we close the popover when
          // it is clicked.
          setActiveFilter(name)
          setEditing(true)
        }}
        ref={(element) => {
          if (isActive && element && filterRef) {
            filterRef.current = element
          }
        }}
      >
        <div className='caption xs:body-sm leading-snug text-gray-500'>{label}</div>
        {value
          ? (
            <div className='body-sm-bold xs:body-bold leading-snug text-gray-800'>
              <span className='sr-only'>Value: </span>
              {value}
            </div>
            )
          : (
            <div className='body-sm xs:body leading-snug text-gray-800'>
              <span className='sr-only'>Placeholder: </span>
              {placeholder}
            </div>
            )}
      </button>
      {value && (
        <div
          className={clsx(
            'absolute top-0 right-2 h-full flex items-center transition'
          )}
        >
          <button
            aria-label={`Clear value for "${label}"`}
            className='appearance-none w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-800 transition outline-2 outline-offset-2 outline-transparent focus-visible:outline-thatch-500'
            disabled={!value}
            onClick={() => {
              onClearValue?.()
            }}
          >
            <XIcon size={16} />
          </button>
        </div>
      )}
      {renderChildren()}
    </div>
  )
}

Filter.displayName = 'MultistepFilters.Filter'
